<script setup lang="ts">
import { useSurveyDragDropOperations } from "@/composition/builder/useSurveyDragDropOperations";
import { useAppStore } from "@/store/app";
import { localizeLegacyNames } from "@/util/localize";
import {
  buildClonedSlideDataAndTasks,
  getAllFontsImport,
  getElementDOMUniqueId,
  scrollContainerBySlideHeight,
  focusLabelFromUserEvent,
  focusLabelAfterActiveChange,
  addActiveTabLoopListener,
  removeActiveTabLoopListener,
} from "@/util/methods";
import {
  AlertCircleIcon,
  Copy01Icon,
  PlusCircleIcon,
  Settings02Icon,
  Trash04Icon,
} from "@gohighlevel/ghl-icons/24/outline";
import { UITooltip } from "@gohighlevel/ghl-ui";
import { cloneDeep } from "lodash";
import { storeToRefs } from "pinia";
import { computed, onBeforeUnmount, onMounted, ref, watch, nextTick } from "vue";
import { VueDraggableNext } from "vue-draggable-next";
import { useI18n } from "vue-i18n";
import AlertModal from "../common/AlertModal.vue";
import BuilderBranding from "../common/BuilderBranding.vue";
import ContactAssociationSettingsModal from "../common/ContactAssociationSettingsModal.vue";
import CustomFieldsPreview from "../common/CustomFieldsPreview.vue";
import Footer from "../common/Footer.vue";
import HLImage from "../common/HLImage.vue";
import MixedCustomObjectErrorModal from "../common/MixedCustomObjectErrorModal.vue";
import StandardFieldPreview from "../common/StandardFieldPreview.vue";
const { t } = useI18n();
const store = useAppStore();
const { elements } = storeToRefs(store);
const currentElement = ref({ label: "" });
const globalScriptCSSStyle = ref({ textContent: null });
const globalScriptCSSLabelHolder = ref({ textContent: null });
const globalScriptCSSPlaceHolder = ref({ textContent: null });
const globalScriptCSSFieldBGColor = ref({ textContent: null });
const globalScriptCSSFieldBorder = ref({ textContent: null });
const globalScriptCSSFieldShadow = ref({ textContent: null });
const globalScriptCSSFieldWidth = ref({ textContent: null });
const globalScriptCSSFieldPadding = ref({ textContent: null });
const globalScriptCSSFontHolder = ref({ textContent: null });

const globalScriptCSSShortLabelHolder = ref({ textContent: null });
const fullWidthFields = [
  "img",
  "submit",
  "h1",
  "html",
  "captcha",
  "large_text",
  "source",
  "file_upload",
];
const openAlertModal = ref(false);
const slideDeleteMessage = ref("Are you sure you want to delete store slide?");
const slideToBeRemoved = ref(undefined);

const formWidth = computed(() => {
  return store.deviceMode === "mobile" ? 400 : store.formStyle.width;
});

const progressBarWidth = computed(() => {
  return (slideIndex: number) => {
    const progress =
      slideIndex === store.slides.length - 1 ? 95 : ((slideIndex + 1) * 100) / store.slides.length;
    return `${progress}%`;
  };
});

const formStyle = computed(() => {
  const style = store.formStyle;
  // Use mobile padding if in mobile mode and it exists, otherwise fallback to desktop padding
  const padding =
    store.deviceMode === "mobile" ? style?.mobilePadding || style?.padding : style?.padding;
  const margin =
    store.deviceMode === "mobile" ? style?.mobileMargin || style?.margin : style?.margin;
  const footerStyle = store.deviceMode === "mobile" ? store.mobileFooterStyle : store.footerStyle;

  // Only apply bottom border radius if footer is not sticky
  const borderRadius = footerStyle?.stickyFooter
    ? `${style?.border?.radius}px ${style?.border?.radius}px 0 0`
    : `${style?.border?.radius}px`;

  return {
    padding: `${padding?.top || 0}px ${padding?.right || 0}px ${padding?.bottom || 0}px ${
      padding?.left || 0
    }px`,
    backgroundColor: `#${style?.background}`,
    border: `${style?.border?.border}px ${style?.border?.style} #${style?.border?.color}`,
    borderRadius: borderRadius,
    boxShadow: `${style?.shadow?.horizontal}px ${style?.shadow?.vertical}px ${style?.shadow?.blur}px ${style?.shadow?.spread}px #${style?.shadow?.color}`,
    width: `${formWidth.value}px`,
  };
});

const containerMarginTop = computed(() => {
  const style = store.formStyle;
  const margin =
    store.deviceMode === "mobile" ? style?.mobileMargin || style?.margin : style?.margin;
  const normalize = (v) => (v === "auto" || v === undefined || v === null ? 20 : v);

  return `${normalize(margin?.top)}px`;
});

const containerMarginBottom = computed(() => {
  const style = store.formStyle;
  const margin =
    store.deviceMode === "mobile" ? style?.mobileMargin || style?.margin : style?.margin;
  const normalize = (v) => (v === "auto" || v === undefined || v === null ? 20 : v);

  return `${normalize(margin?.bottom)}px`;
});

const clickedOnElement = async (e, element, index, slideIndex) => {
  e.stopPropagation();
  const alreadyActive =
    store.currentActiveIndex[0] === slideIndex && store.currentActiveIndex[1] === index;

  if (!alreadyActive) {
    store.currentActiveIndex = [slideIndex, index];
    store.currentSlideIndex = slideIndex;
  }

  if (
    !store.sidebarOverlaySettings ||
    store.sidebarOverlaySettings.index !== index ||
    store.sidebarOverlaySettings.slideIndex !== slideIndex
  ) {
    store.sidebarOverlaySettings = { index, slideIndex, formIndex: undefined };
  }

  currentElement.value = element;
  store.isAnyElementActive = true;
  await nextTick();
  if (!alreadyActive) {
    focusLabelFromUserEvent(e);
  }
};

const fieldStyle = computed(() => {
  const fieldStyleStore = store.fieldStyle;
  const style = {
    padding: `${fieldStyleStore?.padding?.top}px ${fieldStyleStore?.padding?.right}px ${fieldStyleStore?.padding?.bottom}px ${fieldStyleStore?.padding?.left}px`,
    backgroundColor: "#" + fieldStyleStore?.bgColor,
    border: `${fieldStyleStore?.border?.border}px ${
      fieldStyleStore?.border?.type === "none" ? "" : fieldStyleStore?.border?.type
    } #${fieldStyleStore?.border?.color}`,
    borderRadius: fieldStyleStore?.border?.radius + "px",
    boxShadow: `${fieldStyleStore?.shadow?.horizontal}px ${fieldStyleStore?.shadow?.vertical}px ${fieldStyleStore?.shadow?.blur}px ${fieldStyleStore?.shadow?.spread}px #${fieldStyleStore?.shadow?.color}`,
    width: fieldStyleStore?.width + "px",
    color: "#" + fieldStyleStore?.labelColor,
    font: `${fieldStyleStore.labelFontSize}px  ${fieldStyleStore.labelFontWeight} ${fieldStyleStore.labelFontFamily}`,
  };
  return style;
});

onMounted(() => {
  const globalScriptCSS = document.createElement("style");
  globalScriptCSS.type = "text/css";

  // Google and bunny font css script
  const globalScriptFontFamily = document.createElement("style");
  globalScriptFontFamily.type = "text/css";
  globalScriptFontFamily.appendChild(document.createTextNode(""));
  globalScriptCSSFontHolder.value = globalScriptFontFamily.childNodes[0];
  document.head.appendChild(globalScriptFontFamily);

  for (let i = 0; i < 9; i++) {
    globalScriptCSS.appendChild(document.createTextNode(""));
  }
  globalScriptCSSStyle.value = globalScriptCSS.childNodes[0];
  //For Label CSS Change
  globalScriptCSSLabelHolder.value = globalScriptCSS.childNodes[1];
  //For Placeholder CSS Change
  globalScriptCSSPlaceHolder.value = globalScriptCSS.childNodes[2];
  globalScriptCSSFieldBGColor.value = globalScriptCSS.childNodes[3];
  globalScriptCSSFieldBorder.value = globalScriptCSS.childNodes[4];
  globalScriptCSSFieldShadow.value = globalScriptCSS.childNodes[5];
  globalScriptCSSFieldWidth.value = globalScriptCSS.childNodes[6];
  globalScriptCSSFieldPadding.value = globalScriptCSS.childNodes[7];
  globalScriptCSSShortLabelHolder.value = globalScriptCSS.childNodes[8];

  document.head.appendChild(globalScriptCSS);
  globalScriptCSSFontHolder.value.textContent = getAllFontsImport(
    store.fontCollection,
    store.builderConfig.isGDPRCompliant
  );
  setGlobalStyleInHTMl();
  // Localize any legacy default slide names like "Slide N"
  localizeLegacyNames(store.slides, t, /^Slide\s+\d+$/i, "survey.slideLabel");
  addActiveTabLoopListener();
});

onBeforeUnmount(() => {
  removeActiveTabLoopListener();
});

// When the active element changes (via click, clone, or programmatic),
// try to auto-focus its label
watch(
  () => store.currentActiveIndex,
  async () => {
    await nextTick();
    focusLabelAfterActiveChange();
  },
  { deep: true }
);

// const standardSidebarEnabled = (e) => {
//   const modals = document.getElementsByClassName("n-modal-container");
//   const colorPickers = document.getElementsByClassName("v-binder-follower-container");
//   const borderTypes = document.getElementsByClassName("v-binder-follower-content");
//   const modalByTiny = document.getElementsByClassName("tox-dialog");
//   let isClickedOnModal = false;
//   Array.prototype.forEach.call(modals, (modal) => {
//     if (modal.contains(e.target)) {
//       isClickedOnModal = true;
//     }
//   });
//   let isClickedOnColorPicker = false;
//   Array.prototype.forEach.call(colorPickers, (colorPicker) => {
//     if (colorPicker?.contains(e.target)) {
//       isClickedOnColorPicker = true;
//     }
//   });
//   let isClickedOnBorderType = false;
//   Array.prototype.forEach.call(borderTypes, (borderType) => {
//     if (borderType?.contains(e.target)) {
//       isClickedOnBorderType = true;
//     }
//   });
//   let tinyPopUp = false;
//   Array.prototype.forEach.call(modalByTiny, (tiny) => {
//     if (tiny?.contains(e.target)) {
//       tinyPopUp = true;
//     }
//   });
//   console.log(store.formSidebarElement?.contains(e.target), "FOmr");
//   if (
//     !isClickedOnModal &&
//     !isClickedOnColorPicker &&
//     !isClickedOnBorderType &&
//     !tinyPopUp &&
//     !store.formSidebarElement?.contains(e.target)
//   ) {
//     store.currentCustomSidebarPreview = undefined;
//     store.sidebarOverlaySettings = undefined;
//     store.isAnyElementActive = false;
//     allSlideInactive();
//   }
// };

const setGlobalStyleInHTMl = () => {
  const fieldStyle = store.fieldStyle;
  const formStyle = store.formStyle;
  const isMobile = store.deviceMode === "mobile";

  const getMobileOrDesktop = (mobileVal, desktopVal) =>
    isMobile ? mobileVal || desktopVal : desktopVal;

  const labelColor = getMobileOrDesktop(fieldStyle.mobileLabelColor, fieldStyle.labelColor);
  const labelFontSize = getMobileOrDesktop(
    fieldStyle.mobileLabelFontSize,
    fieldStyle.labelFontSize
  );
  const labelFontWeight = getMobileOrDesktop(
    fieldStyle.mobileLabelFontWeight,
    fieldStyle.labelFontWeight
  );
  const labelFontFamily = getMobileOrDesktop(
    fieldStyle.mobileLabelFontFamily,
    fieldStyle.labelFontFamily
  );

  const placeholderColor = getMobileOrDesktop(
    fieldStyle.mobilePlaceholderColor,
    fieldStyle.placeholderColor
  );
  const placeholderFontFamily = getMobileOrDesktop(
    fieldStyle.mobilePlaceholderFontFamily,
    fieldStyle.placeholderFontFamily
  );
  const placeholderFontSize = getMobileOrDesktop(
    fieldStyle.mobilePlaceholderFontSize,
    fieldStyle.placeholderFontSize
  );
  const placeholderFontWeight = getMobileOrDesktop(
    fieldStyle.mobilePlaceholderFontWeight,
    fieldStyle.placeholderFontWeight
  );

  const shortLabelColor = getMobileOrDesktop(
    fieldStyle.shortLabel?.mobileColor,
    fieldStyle.shortLabel?.color
  );
  const shortLabelFontSize = getMobileOrDesktop(
    fieldStyle.shortLabel?.mobileFontSize,
    fieldStyle.shortLabel?.fontSize
  );
  const shortLabelFontWeight = getMobileOrDesktop(
    fieldStyle.shortLabel?.mobileFontWeight,
    fieldStyle.shortLabel?.fontWeight
  );
  const shortLabelFontFamily = getMobileOrDesktop(
    fieldStyle.shortLabel?.mobileFontFamily,
    fieldStyle.shortLabel?.fontFamily
  );

  globalScriptCSSLabelHolder.value.textContent = `#_builder-form label, #_builder-form .label-input {
      color: #${labelColor};
      font-size: ${labelFontSize}px;
      font-weight: ${labelFontWeight};
      font-family: '${labelFontFamily}';
      line-height: 1.8;
    }
    #_builder-form .tnc {
      font-family: '${labelFontFamily}';
    }
    #_builder-form pre {
      white-space: pre-wrap;
      word-break: break-word;
      max-width: 100%;
    }
    /* Bubble containers should inherit label typography */
    #_builder-form .bubble-container, #_builder-form .bubble-checkbox-container {
      font-family: '${labelFontFamily}';
      font-size: ${labelFontSize}px;
      font-weight: ${labelFontWeight};
    }
    `;

  globalScriptCSSPlaceHolder.value.textContent = `#_builder-form ::placeholder, #_builder-form .form-dropdown a, 
    #_builder-form .form-dropdown .dropdown-toggle, #_builder-form .form-placeholder , #_builder-form .currency-code { 
      color : #${placeholderColor}; 
      font-family:'${placeholderFontFamily}'; 
      font-size:${placeholderFontSize}px; 
      font-weight: ${placeholderFontWeight};
        width: 100%;
    }
    /* Bubble labels use placeholder color on label text */
    #_builder-form .bubble-label, #_builder-form .bubble-checkbox-label {
      color: #${placeholderColor} !important;
      background-color: #${fieldStyle.bgColor} !important;
      font-family:'${placeholderFontFamily}' !important;
      font-size:${placeholderFontSize}px !important;
      font-weight:${placeholderFontWeight} !important;
    }`;
  globalScriptCSSFieldBGColor.value.textContent = `#_builder-form .form-control, .form-control:disabled, .form-control[readonly], #_builder-form .form-dropdown{ background-color : #${fieldStyle.bgColor}}`;
  globalScriptCSSFieldBorder.value.textContent =
    "  #_builder-form .form-control, .form-control:disabled, .form-control[readonly], #_builder-form .form-dropdown { border:" +
    fieldStyle.border.border +
    "px " +
    fieldStyle.border.type +
    " #" +
    fieldStyle.border.color +
    ";border-radius:" +
    fieldStyle.border.radius +
    "px ; }" +
    ` .line-form-style { border-bottom: ${fieldStyle.border.border}px ${fieldStyle.border.type} #${fieldStyle.border.color} !important; background-color: #${fieldStyle.bgColor} !important;}` +
    ` #_builder-form .bubble-container, #_builder-form .bubble-checkbox-container { border: ${fieldStyle.border.border}px ${fieldStyle.border.type} #${fieldStyle.border.color} !important; border-radius: 12px !important; }`;
  globalScriptCSSFieldPadding.value.textContent =
    "  #_builder-form .form-control, .form-control:disabled, .form-control[readonly], #_builder-form .form-dropdown { padding:" +
    fieldStyle.padding.top +
    "px " +
    fieldStyle.padding.right +
    "px " +
    fieldStyle.padding.bottom +
    "px " +
    fieldStyle.padding.left +
    "px}" +
    ` #_builder-form .bubble-container, #_builder-form .bubble-checkbox-container { padding: ${fieldStyle.padding.top}px ${fieldStyle.padding.right}px ${fieldStyle.padding.bottom}px ${fieldStyle.padding.left}px !important; background-clip: inherit !important; margin-left: 2px !important; margin-right: 2px !important; }`;
  globalScriptCSSFieldShadow.value.textContent =
    "  #_builder-form .form-control, .form-control:disabled, .form-control[readonly], #_builder-form .form-dropdown { box-shadow:" +
    fieldStyle.shadow.horizontal +
    "px " +
    fieldStyle.shadow.vertical +
    "px " +
    fieldStyle.shadow.blur +
    "px " +
    fieldStyle.shadow.spread +
    "px #" +
    fieldStyle.shadow.color +
    "}" +
    ` #_builder-form .bubble-container, #_builder-form .bubble-checkbox-container { box-shadow: ${fieldStyle.shadow.horizontal}px ${fieldStyle.shadow.vertical}px ${fieldStyle.shadow.blur}px ${fieldStyle.shadow.spread}px #${fieldStyle.shadow.color}; }`;
  globalScriptCSSFieldWidth.value.textContent =
    "  #_builder-form .form-control, .form-control:disabled, .form-control[readonly], #_builder-form .form-dropdown {max-width:" +
    fieldStyle.width +
    "px}";
  globalScriptCSSShortLabelHolder.value.textContent = `#_builder-form .short-label, #_builder-form .short-label::placeholder {
      color: #${shortLabelColor} !important;
      font-size: ${shortLabelFontSize}px !important;
      font-weight: ${shortLabelFontWeight};
      font-family: '${shortLabelFontFamily}';
  }`;
  const styleStore = formStyle.customStyle.split("}");
  let styleString = "";
  styleStore.map(function (value: string) {
    if (value.includes("footer")) {
      styleString += `${value}}`;
    } else if (value !== "" && value.length > 0) {
      styleString += "#_builder-form " + value + "} ";
    }
  });
  globalScriptCSSStyle.value.textContent = styleString;
};

//Store Subscription to watch on store value change using patch
store.$subscribe(() => {
  setGlobalStyleInHTMl();
});

watch([store.fontCollection, () => store.builderConfig.isGDPRCompliant], () => {
  globalScriptCSSFontHolder.value.textContent = getAllFontsImport(
    store.fontCollection,
    store.builderConfig.isGDPRCompliant
  );
});

const updateElementProperty = (property: string, value: string, elementId: string) => {
  store.slides.forEach((slide) => {
    slide.slideData.forEach((element) => {
      const matchUuid = typeof element.uuid === "string" && element.uuid === elementId;
      const matchHidden =
        typeof element.hiddenFieldQueryKey === "string" &&
        element.hiddenFieldQueryKey === elementId;
      if (matchUuid || matchHidden) {
        (element as any)[property] = value;
      }
    });
  });
};

const updateLabel = (newLabel: string, elementId: string) => {
  updateElementProperty("label", newLabel, elementId);
  store.anyUnsavedChanges = true;
};

const updatePlaceholder = (newPlaceholder: string, elementId: string) => {
  updateElementProperty("placeholder", newPlaceholder, elementId);
  store.anyUnsavedChanges = true;
};

const updateShortLabel = (newShortLabel: string, elementId: string) => {
  updateElementProperty("shortLabel", newShortLabel, elementId);
  store.anyUnsavedChanges = true;
};

const updateLowestRating = (lowestRating: string, elementId: string) => {
  updateElementProperty("lowestRating", lowestRating, elementId);
  store.anyUnsavedChanges = true;
};

const updateHighestRating = (highestRating: string, elementId: string) => {
  updateElementProperty("highestRating", highestRating, elementId);
  store.anyUnsavedChanges = true;
};

const isActiveElement = (index, slideIndex) => {
  return store.currentActiveIndex[0] === slideIndex && store.currentActiveIndex[1] === index;
};

const { changeLog, addSlideBelow } = useSurveyDragDropOperations();

const slideChange = (e) => {
  allSlideInactive();
  store.isAnyElementActive = false;
  store.anyUnsavedChanges = true;
};

const getSuccessorIndex = () => {
  return t("survey.slideLabel", { n: store.slides.length + 1 });
};

const allSlideInactive = () => {
  store.currentSlideIndex = undefined;
  store.currentActiveIndex = [undefined, undefined];
};

const removeSlide = (slideIndex) => {
  openAlertModal.value = true;
  slideToBeRemoved.value = slideIndex;
  if (slideIndex < store.slides.length - 1) {
    slideDeleteMessage.value = `${t("survey.slideDeleteBetweenAlertMessage")}`;
  } else {
    slideDeleteMessage.value = `${t("survey.slideDeleteAlertMessage")}`;
  }
};

const openSlideSettings = (slideIndex) => {
  store.sidebarOverlaySettings = {
    index: undefined,
    slideIndex: slideIndex,
    formIndex: undefined,
  };
  store.isAnyElementActive = true;
};

const feedback = (modalName, result) => {
  if (result) {
    store.slides.splice(slideToBeRemoved.value, 1);
    store.anyUnsavedChanges = true;
  }
  slideToBeRemoved.value = undefined;
  openAlertModal.value = false;
};
const popModalChange = () => {
  openAlertModal.value = false;
};

// Inline actions now handled directly by icon clicks next to slide name

const clickedOutSideSlide = (e) => {
  store.currentCustomSidebarPreview = undefined;
  store.sidebarOverlaySettings = undefined;
  store.isAnyElementActive = false;
  allSlideInactive();
};

const cloneSlide = (slideIndex: number) => {
  const sourceSlide = store.slides[slideIndex];
  if (!sourceSlide) {
    return;
  }

  // Only clone elements whose tag is allowed to be duplicated
  const allowedTags = Array.isArray(store.duplicateAllowed) ? store.duplicateAllowed : [];
  const filteredSlide = {
    ...sourceSlide,
    // Keep only cloneable elements:
    // - Standard fields that are allowed
    // - Custom fields with model === contact or no model
    slideData: (sourceSlide.slideData || []).filter((el: any) => {
      const isCustom = el?.id !== undefined || el?.custom === true;
      if (isCustom) {
        return !el?.model || el?.model === "contact";
      }
      return allowedTags.includes(el?.tag);
    }),
  } as any;

  const { clonedSlideData, runCustomFieldCreations } = buildClonedSlideDataAndTasks(
    filteredSlide,
    slideIndex,
    store
  );

  const newSlide = {
    ...cloneDeep(sourceSlide),
    id: new Date().getTime() + "-" + store.slides.length,
    slideName: `${sourceSlide.slideName} (copy)`,
    slideData: clonedSlideData,
  };

  store.slides.splice(slideIndex + 1, 0, newSlide);
  store.anyUnsavedChanges = true;
  store.addToHistory();
  // Scroll by height of next slide minus 20px
  scrollContainerBySlideHeight(
    "form-builder",
    "form-builder-container",
    ".survey-form-container",
    slideIndex + 1,
    20
  );
};

const hasLogicConflict = (slideIndex) => {
  if (store.slides[slideIndex].logic && Object.keys(store.slides[slideIndex].logic).length > 1) {
    return true;
  }
  return false;
};

const applyInlineStyle = () => {
  return {
    marginBottom: store.formStyle.fieldSpacing + "px",
  };
};

/**
 * Handle contact association settings save
 */
const saveContactAssociationSettings = (settings) => {
  store.contactAssociationSettings = settings;
  store.anyUnsavedChanges = true;
  store.showContactAssociationModal = false;
};

/**
 * Handle contact association settings cancel
 */
const cancelContactAssociationSettings = () => {
  store.showContactAssociationModal = false;
};

const shouldRemoveExtraPadding = computed(() => {
  const padding =
    store.deviceMode === "mobile" ? store.formStyle.mobilePadding : store.formStyle.padding;
  return padding?.extraPaddingComputingProcessed === true;
});
</script>

<template>
  <section class="hl_wrapper d-flex survey-wrap-container" @click.prevent="clickedOutSideSlide">
    <section
      id="form-builder"
      class="hl_wrapper--inner form-builder"
      :style="{
        backgroundImage: `url(${
          store.deviceMode === 'mobile'
            ? store.formStyle?.mobileBgImage || store.formStyle?.bgImage
            : store.formStyle?.bgImage
        })`,
        backgroundSize: 'cover',
      }"
    >
      <section
        class="hl_form-builder--main"
        :style="{ marginTop: containerMarginTop, marginBottom: containerMarginBottom }"
      >
        <form id="_builder-form" name="builder-form" class="builder-preview">
          <div class="form-child-wrapper">
            <div class="smooth-dnd-container vertical">
              <VueDraggableNext
                id="form-builder-container"
                :list="store.slides"
                :sort="true"
                group="slide"
                @change="slideChange"
              >
                <div
                  v-for="(itemSlide, indexSlide) in store.slides"
                  :key="indexSlide"
                  class="smooth-dnd-draggable-wrapper"
                >
                  <div
                    class="survey-form-container"
                    :style="{
                      paddingBottom: indexSlide === store.slides.length - 1 ? '200px' : 0,
                    }"
                  >
                    <div>
                      <div
                        class="builder-form-name mb-1 flex h-10 items-center justify-between justify-items-center text-center"
                        :style="{ maxWidth: formWidth + 'px' }"
                      >
                        <div class="flex items-center text-sm font-semibold">
                          {{ itemSlide?.slideName }}
                          <UITooltip
                            v-if="hasLogicConflict(indexSlide)"
                            :style="{ maxWidth: '300px' }"
                            trigger="hover"
                            placement="top"
                          >
                            <template #trigger>
                              <AlertCircleIcon class="ml-1 h-4 w-4 text-red-400"></AlertCircleIcon>
                            </template>

                            {{ t("survey.slideConflictAlertMessage") }}
                          </UITooltip>
                        </div>
                        <div class="ml-2 flex items-center gap-2">
                          <UITooltip trigger="hover" placement="top">
                            <template #trigger>
                              <Settings02Icon
                                class="focus_outline_none h-5 w-5 cursor-pointer text-gray-500 ring-0 hover:text-gray-700 focus:outline-none focus:ring-0 active:outline-none"
                                @mousedown.stop
                                @click.stop="openSlideSettings(indexSlide)"
                              />
                            </template>
                            {{ t("survey.settings") }}
                          </UITooltip>
                          <UITooltip trigger="hover" placement="top">
                            <template #trigger>
                              <Copy01Icon
                                class="focus_outline_none h-5 w-5 cursor-pointer text-gray-500 ring-0 hover:text-gray-700 focus:outline-none focus:ring-0 active:outline-none"
                                @mousedown.stop
                                @click.stop="cloneSlide(indexSlide)"
                              />
                            </template>
                            {{ t("survey.cloneSlide") }}
                          </UITooltip>
                          <UITooltip trigger="hover" placement="top">
                            <template #trigger>
                              <Trash04Icon
                                class="focus_outline_none h-5 w-5 cursor-pointer text-gray-500 ring-0 hover:text-gray-700 focus:outline-none focus:ring-0 active:outline-none"
                                :class="{
                                  'cursor-not-allowed opacity-50': store.slides.length === 1,
                                }"
                                @mousedown.stop
                                @click.stop="
                                  store.slides.length === 1 ? null : removeSlide(indexSlide)
                                "
                              />
                            </template>
                            {{ t("survey.delete") }}
                          </UITooltip>
                        </div>
                      </div>
                      <!-- Mobile Progress Bar -->
                      <div
                        v-if="
                          store.deviceMode === 'mobile' &&
                          store.mobileFooterStyle?.enableProgressBar
                        "
                        class="mobile-progress-bar-overlay"
                        :style="{
                          backgroundColor: `#${store.mobileFooterStyle.progressBarStyle.inactiveFillColor}`,
                          maxWidth: formWidth + 'px',
                        }"
                      >
                        <div
                          class="mobile-progress-fill"
                          :style="{
                            width: progressBarWidth(indexSlide),
                            backgroundColor: `#${store.mobileFooterStyle.progressBarStyle.completeFillColor}`,
                          }"
                        ></div>
                      </div>
                      <div
                        v-if="
                          store.formAction?.headerImageSrc?.trim().length > 0 ||
                          store.formAction?.mobileHeaderImageSrc?.trim().length > 0
                        "
                        class="header-image-container"
                      >
                        <div
                          class="ghl-header-banner w-full max-w-full"
                          :style="{
                            maxWidth: formWidth + 'px',
                          }"
                        >
                          <HLImage
                            :url="
                              store.deviceMode === 'mobile'
                                ? store.formAction?.mobileHeaderImageSrc ||
                                  store.formAction?.headerImageSrc
                                : store.formAction?.headerImageSrc
                            "
                            :width="store.formStyle.width"
                            :alt="t('common.bannerAlt')"
                          ></HLImage>
                        </div>
                      </div>
                      <div
                        id="the-form-builder"
                        class="form-element form-builder--wrap"
                        :class="
                          store.formAction.fieldSettingEnable
                            ? 'one-question-height'
                            : 'normal-height'
                        "
                        :style="formStyle"
                      >
                        <div
                          :class="`drop ${
                            shouldRemoveExtraPadding ? '' : 'builder-padding-remove'
                          } ${
                            store.formAction.fieldSettingEnable
                              ? 'one-question-form-builder-drop'
                              : 'form-builder-drop'
                          }`"
                        >
                          <VueDraggableNext
                            :model-value="itemSlide.slideData"
                            :class="[
                              store.builderConfig.inlineForm
                                ? 'smooth-dnd-container vertical row'
                                : 'smooth-dnd-container vertical',
                              store.formAction.fieldSettingEnable
                                ? 'builder_one_question_container'
                                : 'builder_sub_container',
                            ]"
                            group="elements"
                            :sort="true"
                            @change="(e) => changeLog(e, indexSlide)"
                          >
                            <div
                              v-for="(element, index) in itemSlide.slideData"
                              :key="
                                getElementDOMUniqueId('survey-element', element, index, indexSlide)
                              "
                              class="smooth-dnd-draggable-wrapper element-box menu-field-wrap col-12 form-field-wrapper"
                              :style="applyInlineStyle()"
                              @click="(e) => clickedOnElement(e, element, index, indexSlide)"
                            >
                              <div
                                :class="{ active: isActiveElement(index, indexSlide) }"
                                class="menu-item"
                              >
                                <StandardFieldPreview
                                  v-if="!(element.id !== undefined || element?.custom)"
                                  :key="
                                    getElementDOMUniqueId('std-field', element, index, indexSlide)
                                  "
                                  :element="element"
                                  :index="index"
                                  :slide-index="indexSlide"
                                  :current-element="currentElement"
                                  :field-style="fieldStyle"
                                  :form-label-visible="store.builderConfig.formLabelVisible"
                                  @update:label="(label) => updateLabel(label, element.uuid)"
                                  @update:placeholder="
                                    (placeholder) => updatePlaceholder(placeholder, element.uuid)
                                  "
                                  @update:short-label="
                                    (shortLabel) => updateShortLabel(shortLabel, element.uuid)
                                  "
                                ></StandardFieldPreview>
                                <CustomFieldsPreview
                                  v-if="element.id !== undefined || element?.custom"
                                  :key="
                                    getElementDOMUniqueId(
                                      'custom-field',
                                      element,
                                      index,
                                      indexSlide
                                    )
                                  "
                                  :element="element"
                                  :index="index"
                                  :slide-index="indexSlide"
                                  :current-element="currentElement"
                                  :field-style="fieldStyle"
                                  :form-label-visible="store.builderConfig.formLabelVisible"
                                  :is-form="false"
                                  @update:label="(label) => updateLabel(label, element.uuid)"
                                  @update:placeholder="
                                    (placeholder) => updatePlaceholder(placeholder, element.uuid)
                                  "
                                  @update:short-label="
                                    (shortLabel) => updateShortLabel(shortLabel, element.uuid)
                                  "
                                  @update:lowest-rating="
                                    (lowestRating) => updateLowestRating(lowestRating, element.uuid)
                                  "
                                  @update:highest-rating="
                                    (highestRating) =>
                                      updateHighestRating(highestRating, element.uuid)
                                  "
                                ></CustomFieldsPreview>
                              </div>
                            </div>
                          </VueDraggableNext>
                          <BuilderBranding
                            :is-branding-active="store.formStyle.acBranding"
                            :company="store.company"
                          />
                        </div>
                      </div>
                      <div class="flex justify-center">
                        <Footer
                          v-if="store.footerStyle.stickyFooter"
                          :current-slide-index="indexSlide"
                          :is-last-slide-index="indexSlide === store.slides.length - 1"
                          :style="{ maxWidth: formWidth + 'px' }"
                        />
                        <Footer
                          v-if="!store.footerStyle.stickyFooter && store.deviceMode === 'mobile'"
                          :current-slide-index="indexSlide"
                          :is-last-slide-index="indexSlide === store.slides.length - 1"
                          :style="{ maxWidth: formWidth + 'px' }"
                        />
                      </div>
                      <UITooltip trigger="hover" placement="top">
                        <template #trigger>
                          <PlusCircleIcon
                            class="focus_outline_none mx-auto mt-3 block h-4 w-4 cursor-pointer"
                            @click="addSlideBelow(indexSlide)"
                          ></PlusCircleIcon>
                        </template>
                        {{ t("survey.addSlide") }}
                      </UITooltip>
                    </div>
                  </div>
                </div>
              </VueDraggableNext>
            </div>
          </div>
        </form>
      </section>
    </section>
    <Footer v-if="!store.footerStyle.stickyFooter && store.deviceMode === 'desktop'" />
  </section>
  <AlertModal
    v-if="openAlertModal"
    :title="t('common.alert')"
    :description="slideDeleteMessage"
    modal-name="slideDelete"
    :show-modal-popup="openAlertModal"
    :positive-text="t('common.ok')"
    :negative-text="t('common.cancel')"
    header-type="error"
    footer-type="primary"
    @pop:change="popModalChange"
    @pop:positive="feedback"
    @pop:negative="feedback"
  ></AlertModal>
  <ContactAssociationSettingsModal
    :show="store.showContactAssociationModal"
    :object-model="store.currentCustomObjectModel"
    @update:success="saveContactAssociationSettings"
    @update:cancel="cancelContactAssociationSettings"
  />
  <MixedCustomObjectErrorModal
    :show="store.showMixedCustomObjectErrorModal"
    :existing-object-name="store.mixedCustomObjectErrorInfo.existingObjectName"
    :attempted-object-name="store.mixedCustomObjectErrorInfo.attemptedObjectName"
    @update:close="store.showMixedCustomObjectErrorModal = false"
  />
</template>

<style scoped>
.settings {
  position: absolute;
  top: -40px;
  left: 50%;
  transform: translate(-50%, 0);
  padding: 5px;
  border-radius: 5px;
  background-color: #007bff;
  color: #fff;
  border-color: #007bff;
  z-index: 100;
  position: absolute;
  margin-bottom: 10px;
}

.form-control {
  border-radius: 0.3125rem;
  padding: 15px 20px;
  font-size: 0.875rem;
}
.form-control:disabled,
.form-control [readonly] {
  background-color: #e9ecef;
  opacity: 1;
}

#settings {
  font-size: 10px;
  height: 30px;
}

.builder-form-name {
  margin: 0 auto;
}

.icon-settings {
  display: flex;
  align-items: center;
}

.icon-settings svg {
  padding-right: 5px;
}

/* Force 20px action icon size in header (overrides h-4 emitted classes) */
.builder-form-name svg {
  width: 20px !important;
  height: 20px !important;
}
/* Scoped deep selector to affect child component SVGs */
.builder-form-name :deep(svg) {
  width: 20px !important;
  height: 20px !important;
}

.builder-preview {
  min-height: 450px;
  margin: 0 auto;
  width: 100%;
}

.form-child-wrapper {
  margin: 0 auto;
}

#form-builder {
  background-color: #f3f4f6;
  margin-bottom: 0px;
  overflow: auto;
  margin: 0 auto;
  height: calc(100vh - 52px);
  width: 100%;
}

.add_new_slides {
  text-align: center;
  margin-bottom: 20px;
}

.builder-item {
  cursor: pointer;
  width: 100%;
  background: grey;
  margin: 0.5rem 0;
  min-height: 3rem;
}

.builder_sub_container {
  min-height: 400px;
}
.builder_one_question_container {
  min-height: 140px;
}
#_builder-form .active {
  background: transparent;
  cursor: move;
}

#_builder-form .main_active {
  border: 1px solid #168bf5;
}

.form-builder--wrap {
  margin: 0 auto;
  border-radius: 4px;
  border: 1px dashed #cde0ec;
  padding: 30px 15px;
  background-color: #fff;
  position: relative;
}
.one-question-height {
  min-height: 140px;
}
.normal-height {
  min-height: 450px;
}
.survey-wrap-container .survey-form-container {
  margin: 0 auto;
  padding-top: 10px;
  padding-bottom: 10px;
  position: relative;
}

.form-builder--item {
  margin-bottom: 15px;
  border-radius: 4px;
  border: 2px solid transparent;
  -webkit-transition: border 0.2s ease-in-out;
  -o-transition: border 0.2s ease-in-out;
  transition: border 0.2s ease-in-out;
  position: relative;
}

.builder-inline .smooth-dnd-draggable-wrapper.col-md-6 .f-even .form-builder--item {
  padding-left: 4px !important;
}

.builder-inline .smooth-dnd-draggable-wrapper.col-md-6 .f-odd .form-builder--item {
  padding-left: 10px;
  padding-right: 4px;
}

.builder-inline .smooth-dnd-draggable-wrapper.col-12 .form-builder--item {
  padding-left: 10px;
  padding-right: 4px;
}

.form-builder--item h1,
.form-builder--item h2,
.form-builder--item h3,
.form-builder--item h4,
.form-builder--item h5,
.form-builder--item h6 {
  margin-bottom: 0;
}

.form-builder--item label {
  font-size: 0.875rem;
  color: #2a3135;
}

.captcha-wrap-img {
  width: 335px;
  display: block;
}

.button-element {
  min-width: 85px;
}

.menu-item {
  position: relative;
}

.menu-field-wrap {
  position: relative;
}

#the-form-builder {
  margin: 0 auto;
}

.form-builder-drop {
  min-height: 400px;
}
.one-question-form-builder-drop {
  min-height: 140px;
  padding: 0 15px;
}
.builder-padding-remove {
  padding: 30px 0;
}

.add-dropzone-builder {
  background: transparent;
  width: 100%;
  height: 86px;
  border-width: 1;
  border-style: solid;
  border-color: #e5e7eb;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #6c757d !important;
}

.add-dropzone-builder p {
  margin-left: 5px !important;
}

#form-builder-container {
  height: 100%;
  min-height: 340px;
}

.slide_settings {
  position: absolute;
  right: 130px;
  top: 10px;
}

.survey-form-container a.close-icon-survey {
  position: absolute;
  right: 0px;
  top: 0px;
  -webkit-transition: all 0.5s ease-in;
  transition: all 0.5s ease-in;
  font-size: 10px;
  /* padding: 5px 7px; */
  background: #f5f5f5;
  color: #2a3135;
  border-radius: 50%;
  height: 20px;
  cursor: pointer;
}
.focus_outline_none:focus {
  outline: none;
}
.ghl-header-banner {
  margin: 0 auto;
}

.element-box {
  display: flex;
  flex-flow: column;
  height: 100%;
}

.smooth-dnd-draggable-wrapper {
  cursor: move;
}

.header-image-container {
  position: relative;
  margin: 0 auto;
}

.mobile-progress-bar-overlay {
  position: relative;
  height: 8px;
  border-radius: 4px;
  z-index: 20;
  margin: 0 auto;
}

.mobile-progress-fill {
  height: 100%;
  border-radius: 4px 0 0 0;
  transition: width 0.3s ease-in-out;
}
</style>
<style>
[id^="survey-dropdown-"] .n-dropdown-option-body__suffix {
  display: flex !important;
}
</style>
